import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import AuthProvider from './components/auth/AuthProvider';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
// Removed unused Expenses import
import ExpensesList from './pages/ExpensesList';
import CreateExpense from './pages/CreateExpense';
import ExpenseDetail from './pages/ExpenseDetail';
import ExpenseDashboard from './pages/ExpenseDashboard';
import { ApprovalQueue } from './pages/ApprovalQueue';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import { UsersSettingsPage } from './features/settings/users/UsersSettingsPage';
import { CategoriesSettingsPage } from './features/settings/categories/CategoriesSettingsPage';
import Profile from './pages/Profile';
import GlobalApiErrorListener from './components/system/GlobalApiErrorListener';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Create MUI theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1e40af',
    },
    secondary: {
      main: '#0891b2',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: 'Inter, system-ui, sans-serif',
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
});

function App() {
  return (
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {/* Global listener so API/network errors always surface toasts */}
          <GlobalApiErrorListener />
          <Router>
            <ProtectedRoute>
              <Layout>
                <Routes>
                  {/* Default landing -> Expenses */}
                  <Route path="/" element={<Navigate to="/expenses" replace />} />
                  {/* Admin-only Dashboard */}
                  <Route 
                    path="/dashboard" 
                    element={
                      <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
                        <Dashboard />
                      </ProtectedRoute>
                    } 
                  />
                  
                  {/* Expense Management Routes */}
                  <Route path="/expenses" element={<ExpensesList />} />
                  <Route path="/expenses/dashboard" element={<ExpenseDashboard />} />
                  <Route path="/expenses/create" element={<CreateExpense />} />
                  <Route path="/expenses/:id" element={<ExpenseDetail />} />
                  <Route 
                    path="/approval-queue" 
                    element={
                      <ProtectedRoute requiredRoles={['manager','finance','admin','super_admin']}>
                        <ApprovalQueue />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/approvals" 
                    element={
                      <ProtectedRoute requiredRoles={['manager','finance','admin','super_admin']}>
                        <ApprovalQueue />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/reports" 
                    element={
                      <ProtectedRoute requiredRoles={['manager','finance','admin','super_admin']}>
                        <Reports />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/settings" 
                    element={
                      <ProtectedRoute requiredRoles={['super_admin']}>
                        <Settings />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/settings/users" 
                    element={
                      <ProtectedRoute requiredRoles={['super_admin']}>
                        <UsersSettingsPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/settings/categories" 
                    element={
                      <ProtectedRoute requiredRoles={['super_admin']}>
                        <CategoriesSettingsPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route path="/profile" element={<Profile />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          </Router>
        </ThemeProvider>
      </QueryClientProvider>
    </AuthProvider>
  );
}

export default App;
